import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Canvas, IText, FabricImage } from "fabric";
import { api, assetUrl, errText } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Badge } from "./ui/badge";
import { Loader2, Type, Trash2, ImagePlus, LayoutTemplate } from "lucide-react";
import SafeZoneOverlay from "./SafeZoneOverlay";

const CANVAS_SIZE = 520;
const EXPORT_MULTIPLIER = 1080 / CANVAS_SIZE;
const DEFAULT_COLORS = ["#ffffff", "#000000", "#1E5F6E", "#94AE8F", "#C0913F", "#F9E8E8"];

export default function VisualEditor({ post, open, onClose, onSaved }) {
  const fcRef = useRef(null);
  const disposeRef = useRef(null);
  const [assets, setAssets] = useState([]);
  const [showGuides, setShowGuides] = useState(false);
  const [kitImages, setKitImages] = useState([]);
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [color, setColor] = useState("#ffffff");
  const [size, setSize] = useState(42);
  const [hasSelection, setHasSelection] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data.filter((a) => a.asset_type === "image")));
    api.get("/brand", { params: { brand: post.brand || "" } }).then((r) => {
      const kit = r.data.kit;
      if (kit?.colors?.length) setColors([...new Set([...kit.colors, "#ffffff", "#000000"])].slice(0, 8));
      if (kit?.images?.length) setKitImages(kit.images.slice(0, 8));
    });
  }, [post.brand]);

  const setBackground = async (url) => {
    const canvas = fcRef.current;
    if (!canvas) return;
    try {
      const img = await FabricImage.fromURL(url, { crossOrigin: "anonymous" });
      if (fcRef.current !== canvas) return;
      img.set({ left: 0, top: 0, originX: "left", originY: "top", selectable: false, evented: false,
                scaleX: CANVAS_SIZE / img.width, scaleY: CANVAS_SIZE / img.height });
      img.isBg = true;
      canvas.getObjects().filter((o) => o.isBg).forEach((o) => canvas.remove(o));
      canvas.add(img);
      canvas.sendObjectToBack(img);
      canvas.renderAll();
    } catch {
      toast.error("Couldn't load that image (blocked by its host)");
    }
  };

  const initCanvas = useCallback((container) => {
    if (!container) {
      if (fcRef.current) { disposeRef.current = fcRef.current.dispose(); fcRef.current = null; }
      return;
    }
    (async () => {
      try { await disposeRef.current; } catch { /* disposed */ }
      if (fcRef.current || !container.isConnected) return;
      container.replaceChildren();
      const el = document.createElement("canvas");
      container.appendChild(el);
      const canvas = new Canvas(el, { width: CANVAS_SIZE, height: CANVAS_SIZE, preserveObjectStacking: true, backgroundColor: "#e8e4dc" });
      fcRef.current = canvas;
      const sync = () => {
        const obj = canvas.getActiveObject();
        setHasSelection(!!obj);
        if (obj?.fontSize) setSize(Math.round(obj.fontSize));
        if (obj?.fill) setColor(obj.fill);
      };
      canvas.on("selection:created", sync);
      canvas.on("selection:updated", sync);
      canvas.on("selection:cleared", () => setHasSelection(false));
      if (post.image_url) setBackground(assetUrl(post.image_url));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addText = () => {
    const canvas = fcRef.current;
    const t = new IText("Double-click to edit", {
      left: CANVAS_SIZE / 2, top: CANVAS_SIZE / 2, originX: "center", originY: "center",
      fontFamily: "Fraunces, serif", fontWeight: 600, fontSize: size, fill: color,
      shadow: "rgba(0,0,0,0.45) 0 2px 10px",
    });
    canvas.add(t);
    canvas.setActiveObject(t);
    canvas.renderAll();
  };

  const addLogo = async (url) => {
    const canvas = fcRef.current;
    try {
      const img = await FabricImage.fromURL(assetUrl(url), { crossOrigin: "anonymous" });
      img.scaleToWidth(110);
      img.set({ left: CANVAS_SIZE - 75, top: 75, originX: "center", originY: "center" });
      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
    } catch {
      toast.error("Couldn't load that image");
    }
  };

  const applyColor = (c) => {
    setColor(c);
    const obj = fcRef.current?.getActiveObject();
    if (obj && obj.set && obj.fontSize) { obj.set("fill", c); fcRef.current.renderAll(); }
  };

  const applySize = (v) => {
    setSize(v);
    const obj = fcRef.current?.getActiveObject();
    if (obj?.fontSize) { obj.set("fontSize", v); fcRef.current.renderAll(); }
  };

  const removeSelected = () => {
    const canvas = fcRef.current;
    const obj = canvas?.getActiveObject();
    if (obj) { canvas.remove(obj); canvas.discardActiveObject(); canvas.renderAll(); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const canvas = fcRef.current;
      canvas.discardActiveObject();
      canvas.renderAll();
      const dataUrl = canvas.toDataURL({ format: "png", multiplier: EXPORT_MULTIPLIER });
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append("file", new File([blob], "edited.png", { type: "image/png" }));
      fd.append("asset_type", "image");
      fd.append("brand", post.brand || "");
      const { data: asset } = await api.post("/assets", fd);
      await api.patch(`/posts/${post.id}`, { image_url: asset.url });
      toast.success("Visual updated");
      onSaved(asset.url);
    } catch (e) {
      toast.error(typeof e?.message === "string" && e.message.includes("Tainted") ? "That background blocks editing exports — use an uploaded asset instead" : errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="visual-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">Visual editor {post.brand && <Badge variant="secondary" className="font-sans font-normal">{post.brand}</Badge>}</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-[1fr_280px] gap-6">
          <div>
            <div className="relative rounded-lg border border-border overflow-hidden bg-muted" style={{ width: CANVAS_SIZE, height: CANVAS_SIZE, maxWidth: "100%" }}>
              <div ref={initCanvas} data-testid="editor-canvas" />
              {showGuides && <SafeZoneOverlay wm={{ position: post?.wm_position, scale: post?.wm_scale }} />}
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-[11px] text-muted-foreground">Drag to move · corners to resize/rotate · double-click text to edit</p>
              <button type="button" data-testid="editor-safe-zone-toggle" onClick={() => setShowGuides((v) => !v)}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${showGuides ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <LayoutTemplate size={11} className="inline mr-1 -mt-0.5" /> Safe zones
              </button>
            </div>
          </div>
          <div className="space-y-5 max-h-[560px] overflow-y-auto pr-1">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="editor-add-text-btn" onClick={addText}><Type size={13} className="mr-1.5" /> Add text</Button>
              <Button size="sm" variant="outline" data-testid="editor-delete-btn" disabled={!hasSelection} onClick={removeSelected}><Trash2 size={13} className="mr-1.5" /> Remove</Button>
            </div>
            <div className="space-y-2">
              <Label>Brand colors</Label>
              <div className="flex gap-2 flex-wrap">
                {colors.map((c) => (
                  <button key={c} data-testid={`editor-color-${c.replace("#", "")}`} onClick={() => applyColor(c)}
                    className={`w-7 h-7 rounded-full border-2 ${color === c ? "border-accent scale-110" : "border-border"} transition-transform`} style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Text size · {size}px</Label>
              <Slider value={[size]} min={16} max={110} onValueChange={([v]) => applySize(v)} data-testid="editor-size-slider" />
            </div>
            {(assets.length > 0 || kitImages.length > 0) && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><ImagePlus size={13} /> Backgrounds & logos</Label>
                <p className="text-[11px] text-muted-foreground">Tap image to swap background · "+ add" places it on the canvas (logo)</p>
                <div className="grid grid-cols-4 gap-2 max-h-44 overflow-y-auto">
                  {assets.map((a) => (
                    <div key={a.id} className="space-y-1">
                      <img src={assetUrl(a.url)} alt="" data-testid={`editor-bg-${a.id}`} className="w-full aspect-square object-cover rounded border border-border cursor-pointer hover:opacity-80" onClick={() => setBackground(assetUrl(a.url))} title="Set as background" />
                      <button className="text-[9px] text-accent w-full" onClick={() => addLogo(a.url)}>+ add</button>
                    </div>
                  ))}
                  {kitImages.map((u, i) => (
                    <div key={`kit-${i}`} className="space-y-1">
                      <img src={u} alt="" className="w-full aspect-square object-cover rounded border border-border cursor-pointer hover:opacity-80" onClick={() => setBackground(u)} title="Set as background (from your website)" onError={(e) => { e.target.parentElement.style.display = "none"; }} />
                      <button className="text-[9px] text-accent w-full" onClick={() => addLogo(u)}>+ add</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button className="w-full" data-testid="editor-save-btn" disabled={busy} onClick={save}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : "Apply & save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
